/**
 * Shared scaffolding for the Market analytics dashboards.
 *
 * Every page is a thin component: pick an endpoint, pick charts. The shell
 * handles the window picker, loading skeleton, error state and the
 * consistent Aurora-style header. All data is real AEMO actuals from the
 * backend /api/market/* endpoints (aggregated in BigQuery).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Skeleton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { cachedGet } from '../../utils/cachedFetch';
import { useThemeColors } from '../../theme/ThemeModeContext';
import {
  useContainerWidth,
  REGION_COLORS, REGION_LABELS,
} from '../charts/shared';

// Wider left gutter than charts/shared's CHART_MARGINS_SIMPLE (70) so
// full-width y labels ("A$1.2M", "8,000 MW") don't ellipsize.
const MARGIN = { top: 16, right: 24, left: 10, bottom: 64 };

// @mui/x-charts v8 legend: bottom, horizontal. (The shared.js
// defaultLineLegendProps uses the pre-v8 `direction:'row'` shape which
// triggers a propType warning, so the market charts configure it here.)
const LEGEND_SLOT = { direction: 'horizontal', position: { vertical: 'bottom', horizontal: 'center' } };

export const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const WINDOWS = [
  { code: 90, label: '90D' },
  { code: 180, label: '6M' },
  { code: 365, label: '1Y' },
  { code: 730, label: '2Y' },
];

// Region switch options. 'NEM' = aggregate across all regions (the default,
// one clean line); the rest narrow to a single region.
export const REGION_OPTS = [
  { v: 'NEM', l: 'NEM' },
  { v: 'NSW1', l: 'NSW' },
  { v: 'QLD1', l: 'QLD' },
  { v: 'VIC1', l: 'VIC' },
  { v: 'SA1', l: 'SA' },
  { v: 'TAS1', l: 'TAS' },
];

// Brand amber for the aggregate "NEM" line (the single emphasis series).
export const NEM_COLOR = '#D98A2B';

const tbgSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none', fontWeight: 600, fontSize: '0.76rem',
    px: 1.3, py: 0.4, borderColor: 'divider',
    '&.Mui-selected': {
      bgcolor: 'primary.main', color: '#F4F2EC',
      '&:hover': { bgcolor: 'primary.dark' },
    },
  },
};

/**
 * Collapse a flat region-keyed series into ONE line: either the picked
 * region, or — when region==='NEM' — an aggregate across regions
 * (mean for prices/%, sum for additive quantities, or a custom reducer).
 */
export function regionLine(rows, {
  region, keyField = 'date', valField, agg = 'avg', nemReducer, label, color, fmtKey,
}) {
  const isHour = keyField === 'hour';
  const keys = [...new Set((rows || []).map((r) => r[keyField]))]
    .sort(isHour ? (a, b) => a - b : undefined);
  const values = keys.map((k) => {
    const rk = (rows || []).filter((r) => r[keyField] === k);
    if (region !== 'NEM') {
      const row = rk.find((r) => r.region === region);
      return row ? row[valField] : null;
    }
    if (nemReducer) return nemReducer(rk);
    const nums = rk.map((r) => r[valField]).filter((v) => v != null);
    if (!nums.length) return null;
    const sum = nums.reduce((a, b) => a + b, 0);
    return agg === 'sum' ? sum : sum / nums.length;
  });
  const fk = fmtKey || (isHour ? (k) => `${k}:00` : fmtDate);
  return {
    x: keys.map(fk),
    series: [{ data: values, label, color, showMark: false, curve: 'linear', connectNulls: true }],
  };
}

export function useMarketData(path, days) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (nocache = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await cachedGet(`${API_BASE_URL}/api/market/${path}`, {
        params: { days }, nocache,
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load market data');
    } finally {
      setLoading(false);
    }
  }, [path, days]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: () => load(true) };
}

export function MarketShell({
  title, subtitle, days, setDays, region, setRegion,
  windows = WINDOWS, loading, error, children,
}) {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <Box
        onClick={() => navigate('/market')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/market'); }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2,
          cursor: 'pointer', color: 'text.secondary',
          transition: 'color 0.15s ease',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
          Back to Market
        </Typography>
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 2, mb: 2.5,
      }}>
        <Box>
          <Typography sx={{
            fontSize: '1.5rem', fontWeight: 700, color: 'text.primary',
            letterSpacing: '-0.025em', lineHeight: 1.1,
          }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {setRegion && (
            <ToggleButtonGroup
              size="small" exclusive value={region}
              onChange={(_, v) => v && setRegion(v)}
              sx={tbgSx}
            >
              {REGION_OPTS.map((o) => (
                <ToggleButton key={o.v} value={o.v}>{o.l}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            size="small" exclusive value={days}
            onChange={(_, v) => v && setDays(v)}
            sx={tbgSx}
          >
            {windows.map((w) => (
              <ToggleButton key={w.code} value={w.code}>{w.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={i === 0 ? 96 : 360}
              sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : children}
    </Box>
  );
}

const fmtDate = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};

/**
 * Pivot a flat [{date, <keyField>, <valField>}] list into x-charts series,
 * one series per distinct key, aligned on the sorted union of dates.
 */
export function pivotSeries(rows, keyField, valField, { colors, labels } = {}) {
  const dates = [...new Set(rows.map((r) => r.date))].sort();
  const keys = [...new Set(rows.map((r) => r[keyField]))].sort();
  const idx = new Map(dates.map((d, i) => [d, i]));
  const series = keys.map((k) => {
    const arr = new Array(dates.length).fill(null);
    rows.filter((r) => r[keyField] === k).forEach((r) => {
      arr[idx.get(r.date)] = r[valField];
    });
    return {
      data: arr,
      label: (labels && labels[k]) || k,
      color: colors && colors[k],
      showMark: false,
      curve: 'linear',
      connectNulls: true,
    };
  });
  return { x: dates.map(fmtDate), series, keys };
}

/** Responsive @mui/x-charts LineChart in the house style. */
export function LineChartBox({ x, series, height = 360, yFormat, scaleType = 'point' }) {
  const colors = useThemeColors();
  const { ref, width } = useContainerWidth();
  return (
    <Box ref={ref} sx={{ width: '100%', height, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <LineChart
          width={width}
          height={height}
          series={series}
          xAxis={[{
            scaleType, data: x,
            tickLabelStyle: { fontSize: 11, fill: colors.textDim },
            tickNumber: x.length > 26 ? 13 : undefined,
          }]}
          yAxis={[{ valueFormatter: yFormat, width: 78, tickLabelStyle: { fontSize: 11, fill: colors.textDim } }]}
          margin={MARGIN}
          grid={{ horizontal: true }}
          slotProps={{ legend: LEGEND_SLOT }}
          sx={{
            '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
            '& .MuiChartsAxis-tick': { stroke: colors.hairline2 },
            '& .MuiChartsGrid-line': { stroke: colors.hairline },
          }}
        />
      )}
    </Box>
  );
}

/** Responsive @mui/x-charts BarChart in the house style. */
export function BarChartBox({ x, series, height = 360, yFormat, stacked = false }) {
  const colors = useThemeColors();
  const { ref, width } = useContainerWidth();
  const s = stacked ? series.map((ss) => ({ ...ss, stack: 'total' })) : series;
  return (
    <Box ref={ref} sx={{ width: '100%', height, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <BarChart
          width={width}
          height={height}
          series={s}
          xAxis={[{
            scaleType: 'band', data: x,
            tickLabelStyle: { fontSize: 11, fill: colors.textDim },
          }]}
          yAxis={[{ valueFormatter: yFormat, width: 78, tickLabelStyle: { fontSize: 11, fill: colors.textDim } }]}
          margin={MARGIN}
          grid={{ horizontal: true }}
          slotProps={{ legend: LEGEND_SLOT }}
          sx={{
            '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
            '& .MuiChartsAxis-tick': { stroke: colors.hairline2 },
            '& .MuiChartsGrid-line': { stroke: colors.hairline },
          }}
        />
      )}
    </Box>
  );
}

/** Responsive stacked-area chart (OpenElectricity-style generation mix). */
export function AreaChartBox({ x, series, height = 420, yFormat }) {
  const colors = useThemeColors();
  const { ref, width } = useContainerWidth();
  const s = series.map((ss) => ({
    ...ss, area: true, stack: 'total', showMark: false, curve: 'linear',
  }));
  return (
    <Box ref={ref} sx={{ width: '100%', height, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <LineChart
          width={width}
          height={height}
          series={s}
          xAxis={[{
            scaleType: 'point', data: x,
            tickLabelStyle: { fontSize: 11, fill: colors.textDim },
            tickNumber: 8,
          }]}
          yAxis={[{ valueFormatter: yFormat, width: 78,
            tickLabelStyle: { fontSize: 11, fill: colors.textDim } }]}
          margin={MARGIN}
          grid={{ horizontal: true }}
          slotProps={{ legend: LEGEND_SLOT }}
          sx={{
            '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
            '& .MuiChartsAxis-tick': { stroke: colors.hairline2 },
            '& .MuiChartsGrid-line': { stroke: colors.hairline },
            '& .MuiAreaElement-root': { fillOpacity: 0.85 },
          }}
        />
      )}
    </Box>
  );
}

export { REGION_COLORS, REGION_LABELS };
